import { FC, useCallback, useState } from 'react';
import { App, Button, Table } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PERMISSION_ACTIONS, PERMISSION_ROUTES } from 'constants/permissions';
import { CONFIG_PAGES } from 'constants/configs';
import { useNavigate } from 'react-router-dom';
import { useCheckOrganization } from 'hooks';
import { fetchConfigByType, upsertConfig } from 'services/api/config';

import useOrgEffect from 'hooks/useOrgEffect';
import { OrganizationAlert, Private } from 'components/shared';
import { ActionMenu, PageWrapper } from 'components/ui';
import { HowItWorksModal } from './components';
import { showErrorMessage } from 'utils/showErrorMessage';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type HowItWorksItem = Record<string, any> & { id: string };

const HowItWorks: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { modal } = App.useApp();
  const [selectedId, setSelectedId] = useState('');
  const [isDetailsVisible, setDetailsVisible] = useState(false);
  const { checkOrganizationWrapper } = useCheckOrganization();

  const [results, setResults] = useState<HowItWorksItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useOrgEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const row = await fetchConfigByType(CONFIG_PAGES.HOW_IT_WORKS);
        if (!cancelled) setResults((row?.body?.data as HowItWorksItem[]) ?? []);
      } catch {
        // swallow — empty list on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const onCloseDetails = useCallback(() => {
    setSelectedId('');
    setDetailsVisible(false);
  }, []);

  const triggerRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  const deleteItem = (id: string) => {
    modal.confirm({
      title: 'Warning',
      content: <>Are you sure?</>,
      async onOk() {
        try {
          setIsCreating(true);
          await upsertConfig(CONFIG_PAGES.HOW_IT_WORKS, {
            data: results.filter(item => item.id !== id)
          });
          triggerRefresh();
        } catch (err) {
          showErrorMessage(err);
        } finally {
          setIsCreating(false);
        }
      }
    });
  };

  return (
    <PageWrapper
      title="How It Works"
      color="white"
      extra={[
        <Private
          route={PERMISSION_ROUTES.staticContent}
          action={PERMISSION_ACTIONS.create}
          key="add-how-it-works"
        >
          <OrganizationAlert>
            <Button type="primary" onClick={() => setDetailsVisible(true)}>
              <PlusOutlined />
              Create
            </Button>
          </OrganizationAlert>
        </Private>
      ]}
    >
      <Table
        dataSource={results}
        loading={loading || isCreating}
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Title',
            dataIndex: 'id',
            key: 'title',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            render(_, record: any) {
              return record?.values?.en?.title || '-';
            }
          },
          {
            title: 'Description',
            dataIndex: 'id',
            key: 'description',
            width: '40%',
            render(_, record) {
              return record?.values?.en?.description || '-';
            }
          },
          {
            title: '',
            dataIndex: 'id',
            width: '5%',
            align: 'right',
            render: (_, record) => (
              <ActionMenu
                data={[
                  {
                    title: 'Update content',
                    permission: {
                      route: PERMISSION_ROUTES.staticContent,
                      action: PERMISSION_ACTIONS.update
                    },
                    action() {
                      checkOrganizationWrapper(() => {
                        navigate(`details/${record.id}`);
                      });
                    }
                  },
                  {
                    title: t('table.edit'),
                    permission: {
                      route: PERMISSION_ROUTES.staticContent,
                      action: PERMISSION_ACTIONS.update
                    },
                    action() {
                      setSelectedId(record.id);
                      setDetailsVisible(true);
                    }
                  },
                  {
                    title: t('table.delete'),
                    permission: {
                      route: PERMISSION_ROUTES.staticContent,
                      action: PERMISSION_ACTIONS.delete
                    },
                    action() {
                      deleteItem(record.id);
                    },
                    menuProps: {
                      style: {
                        color: 'var(--danger-color)'
                      }
                    }
                  }
                ]}
                placement="bottomRight"
                icon={<EllipsisOutlined />}
              />
            )
          }
        ]}
        pagination={{
          showSizeChanger: false
        }}
        rowKey="id"
      />
      {isDetailsVisible && (
        <HowItWorksModal
          id={selectedId}
          onClose={onCloseDetails}
          results={results}
          onSaved={triggerRefresh}
        />
      )}
    </PageWrapper>
  );
};

export default HowItWorks;
